/**
 * Copyright (c) 2021, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
 * You may not alter or remove any copyright or other notice from copies of this content.
 */

import React, { FunctionComponent, ReactElement } from "react";
import { Button, Header } from "rsuite";
import logo from "../assets/images/branding/asgardeo-logo.png";
import { useConfig } from "../hooks";
import { IdentifiableComponentInterface } from "../models/core";

/**
 * Application Header props interface.
 */
interface HeaderPropsInterface extends IdentifiableComponentInterface {
    /*
    * Should show a link to navigate back to Asgardeo?
    */
    showGoToAsgardeoLink?: boolean;
    /**
     * Should show sign-in option?
     */
    showSignInButton?: boolean;
    /**
     * Should show sign-out option?
     */
    showSignOutButton?: boolean;
    /**
     * Callback for logout action.
     */
    onSignOut?: () => void;
    /**
     * Callback for sign-in again.
     */
    onSignIn?: () => void;
}

/**
 * Application Header component.
 *
 * @param {AppHeaderPropsInterface} props - Props injected to the component.
 * @return {React.ReactElement}
 */
export const AppHeader: FunctionComponent<HeaderPropsInterface> = (
    props: HeaderPropsInterface
): ReactElement => {

    const {
        showGoToAsgardeoLink,
        showSignInButton,
        showSignOutButton,
        onSignOut,
        onSignIn,
        ["data-componentid"]: componentId
    } = props;

    const { deployment } = useConfig();

    /**
     * callback handler for the logo click event
     */
    const onLogoClick = () => {
        window.open(deployment?.documentation?.baseUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <Header data-componentid={ componentId }>
            <div >
                <img src={ logo } className="logo" onClick={ onLogoClick }></img>
            </div>
            <div className="header-button-group">
                {
                    showGoToAsgardeoLink && (
                        <Button
                            className="header-button-first"
                            appearance="subtle"
                            onClick={ () => {
                                window.open(deployment?.links?.console, "_blank", "noopener,noreferrer");
                            } }
                        >
                            Go to Asgardeo
                        </Button>
                    )
                }
                {
                    showSignOutButton && (
                        <Button
                            className="header-button-second"
                            onClick={ () => onSignOut && onSignOut() }
                            appearance="ghost"
                            color="orange"
                        >
                            Logout
                        </Button>
                    )
                }
                {
                    showSignInButton && (
                        <Button
                            className="header-button-second"
                            onClick={ onSignIn }
                            appearance= "ghost"
                            color="orange"
                        >
                            Sign In
                        </Button>
                    )
                }
            </div>
        </Header>
    );
};

/**
 * Default Props.
 */
AppHeader.defaultProps = {
    showGoToAsgardeoLink: true,
    showSignInButton: false,
    showSignOutButton: false
};
